import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Components
import useToast from '../../components/molecules/Toast/useToast';

// Domains
import { mergeDashboardFilterCards } from '../../domain/dashboardFilters/dashboardFiltersUseCase';

// Queries
import { useDashboardFiltersMutation, DASHBOARD_FILTERS_QUERY_KEY } from './dashboardFiltersQueries';
import { DASHBOARD_QUERY_KEY } from '../dashboard/dashboardQueries';

/**
 * Custom hook for optimistic updates of manager dashboard filters.
 * Once settled, the dashboard is reloaded: /dashboard reads the state posted here.
 * @param {string} path - The dashboard path for the query key
 * @param {import('@tanstack/react-query').UseMutationOptions<ApiManagerDashboardFilters, Error, ApiManagerDashboardFiltersCreate, unknown>} [options = {}] - The mutation options
 * @param {string | null} [contexte] - The context (optional)
 * @returns {import('@tanstack/react-query').UseMutationResult<ApiManagerDashboardFilters, Error, ApiManagerDashboardFiltersCreate, unknown>} The mutation with optimistic updates
 */
export const useDashboardFiltersOptimistic = (path, options = {}, contexte = null) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();
  const queryKey = [DASHBOARD_FILTERS_QUERY_KEY, path, contexte];

  const {
    onSuccess, onError, onSettled, ...restOptions
  } = options;

  return useDashboardFiltersMutation(path, {
    ...restOptions,
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey });

      /** @type {ApiManagerDashboardFilters} */
      const previous = queryClient.getQueryData(queryKey);

      const optimisticDashboardFilters = {
        ...previous,
        filterValues: data?.filterValues ?? previous?.filterValues,
        // POST search omits GET-only fields (e.g. options); merge so the toolbar stays mounted.
        search: previous?.search ? {
          ...previous.search,
          ...data?.search,
        } : previous?.search,
        personalViews: {
          ...previous?.personalViews,
          selectedOption: (() => {
            const value = data?.personalViews?.selectedOption ?? previous?.personalViews?.selectedOption;
            return value === '' ? null : value;
          })(),
        },
        cards: mergeDashboardFilterCards(previous?.cards, data?.cards),
      };

      queryClient.setQueryData(queryKey, () => ({ ...optimisticDashboardFilters }));

      return { previous };
    },
    onError: (
      /** @type {Error} */ error,
      /** @type {ApiManagerDashboardFiltersCreate} */ variables,
      /** @type {{ previous?: ApiManagerDashboardFilters } | undefined} */ context,
    ) => {
      queryClient.setQueryData(queryKey, () => context?.previous);

      notify({
        message: error?.message || t('toast.dashboardFilters.error'),
        type: 'alert',
      });
      onError?.(error, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEY], type: 'active' });
      onSettled?.(data, error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
  }, contexte);
};
